import { useEffect as useDrawerBaselineEffect, useEffectEvent as useDrawerBaselineEvent } from "react";
import { useDirtySnapshot } from "../../../../../../../shared/frontend/forms/useDirtySnapshot";
import { formSnapshot } from "../../../../../../../shared/frontend/forms/formSnapshot";
import { cssSpacing, spacing, spacing as designSpacing, borderRadius as designBorderRadius } from "../../../app/design-system";
import { DeleteOutlined } from "@ant-design/icons";
import { App, Button, Form, InputNumber, Select, Tooltip, Typography, Checkbox } from "../../../../../../../shared/frontend/ui";
import type { Dayjs } from "dayjs";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import dayjs from "dayjs";
import React from "react";
import { TIME_UNIT_DAYS,TIME_UNIT_MONTHS,TIME_UNIT_WEEKS,TIME_UNIT_YEARS } from "../../../app/constants/timeUnit";
import { WALLET_TYPE_POINTS, WALLET_TYPE_VALUE } from "../../../app/constants/walletType";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatNumber,formatWalletBalance } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { useLastPaymentMethod } from "../../../app/prefs/useLastPaymentMethod";
import { paymentMethodDefinitionSelectOptions,type PaymentMethodDefinitionLike } from "../../../app/utils/paymentMethod";
import { FormDrawer } from "../../../components/FormDrawer";
import { FormSectionHeading } from "../../../components/FormSectionHeading";
import { ResponsiveFormGrid } from "../../../components/ui/antd";
import type { PersonDto } from "../types";
import { PersonWalletHistorySection,type WalletHistoryRow } from "./PersonWalletHistorySection";
import type { WalletCreateFormValues,WalletProductDto,WalletRow } from "./personDetailTypes";
import { formatDateOnlyIso } from "./personDetailUtils";
import { loadPersonDetailRecord,type PersonDetailApiClient } from "./usePersonDetailRecord";
import { DatePicker } from "../../../../../../../shared/frontend/components/ControlledPickers";
import { ACCESS_SCOPE_ANY_STUDIO } from "../../../app/constants/accessScope";

type ApiClient = PersonDetailApiClient & {
  del: <T = unknown>(url: string) => Promise<T>;
  post: <T = unknown>(url: string, body?: unknown) => Promise<T>;
};

type MessageApi = {
  error: (content: React.ReactNode) => void;
  success: (content: React.ReactNode) => void;
  warning: (content: React.ReactNode) => void;
};

type ModalApi = {
  confirm: ReturnType<typeof App.useApp>["modal"]["confirm"];
};

type PersonWalletEditDrawerProps = {
  canWriteFinance: boolean;
  open: boolean;
  walletEdit: WalletRow | null;
  form: FormInstance<WalletCreateFormValues>;
  saving: boolean;
  setSaving: (saving: boolean) => void;
  isLocked: boolean;
  setIsLocked: (isLocked: boolean) => void;
  walletProducts: WalletProductDto[];
  walletBalanceTouched: boolean;
  setWalletBalanceTouched: (touched: boolean) => void;
  selectedWalletProduct: WalletProductDto | null;
  onWalletProductChange: (walletProductId: string) => void;
  adjustAmount: number | null;
  setAdjustAmount: (amount: number | null) => void;
  history: WalletHistoryRow[];
  historyLoading: boolean;
  api: ApiClient;
  message: MessageApi;
  modal: ModalApi;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  personId: string;
  homeStudioId?: string | null;
  decimalSeparator: string;
  currencyCode: string;
  moneyPlaceholder: string;
  dateInputFormat: string | string[];
  onClose: () => void;
  onDeleted: () => void;
  onSaved: (updatedPerson: PersonDto | null) => void;
  onReloadWalletAndHistory: () => Promise<void>;
  onReloadWalletsAfterBalanceAdjustment: () => void;
};

export function PersonWalletEditDrawer({
  canWriteFinance,
  open,
  walletEdit,
  form,
  saving,
  setSaving,
  isLocked,
  setIsLocked,
  walletProducts,
  walletBalanceTouched,
  setWalletBalanceTouched,
  selectedWalletProduct,
  onWalletProductChange,
  adjustAmount,
  setAdjustAmount,
  history,
  historyLoading,
  api,
  message,
  modal,
  text,
  units,
  personId,
  homeStudioId,
  decimalSeparator,
  currencyCode,
  moneyPlaceholder,
  dateInputFormat,
  onClose,
  onDeleted,
  onSaved,
  onReloadWalletAndHistory,
  onReloadWalletsAfterBalanceAdjustment
}: PersonWalletEditDrawerProps) {
  const walletType = Number(walletEdit ? walletEdit.walletType : selectedWalletProduct?.type || 0);
  const isPointsWallet = walletType === WALLET_TYPE_POINTS;
  const currentBalance = Number(form.getFieldValue("balance") ?? walletEdit?.balance ?? 0);
  const watchedBalance = Form.useWatch("balance", form);

  // Zahlungsart-Optionen für Geld-Wertekonten laden (auch die erste Aufladung ist eine Zahlung).
  const [paymentMethodDefs, setPaymentMethodDefs] = React.useState<PaymentMethodDefinitionLike[]>([]);
  const [adjustPaymentMethod, setAdjustPaymentMethod] = React.useState<number | null>(null);
  const walletFields = Form.useWatch(formSnapshot, { form: form, preserve: true });
  const { dirty: walletDirty, markSaved: walletMarkSaved } = useDirtySnapshot({ fields: walletFields, extra: { isLocked, adjustAmount, adjustPaymentMethod } });
  const walletInitializeDirty = useDrawerBaselineEvent(() => walletMarkSaved({ fields: formSnapshot(form.getFieldsValue(true)), extra: { isLocked, adjustAmount, adjustPaymentMethod } }));

  React.useEffect(() => {
    if (!open || isPointsWallet) return;
    let alive = true;
    api
      .get<PaymentMethodDefinitionLike[]>("/api/v1/payment-method-definitions")
      .then((list) => {
        if (alive) setPaymentMethodDefs(list || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [api, open, isPointsWallet]);
  const paymentMethodOptions = React.useMemo(
    () => paymentMethodDefinitionSelectOptions(paymentMethodDefs, true),
    [paymentMethodDefs]
  );
  const { rememberPaymentMethod, resolveDefaultPaymentMethod } = useLastPaymentMethod();

  React.useEffect(() => {
    if (!open) setAdjustPaymentMethod(null);
  }, [open]);

  /**
   * Zuletzt selbst erfasste Zahlungsart als Vorbelegung. Abgeleitet statt gesetzt: Die Zahlarten
   * kommen asynchron, und ein Zustandswechsel im Effekt löste nur eine zweite Renderrunde aus.
   * Sobald der Benutzer selbst wählt, gilt seine Auswahl.
   */
  const presetPaymentMethod = open ? resolveDefaultPaymentMethod(paymentMethodOptions, null) : null;
  const effectiveAdjustPaymentMethod = adjustPaymentMethod ?? presetPaymentMethod;

  // Die erste Aufladung läuft über das Formular; dort kann die Vorbelegung nur gesetzt werden.
  const applyDefaultPaymentMethod = React.useEffectEvent(() => {
    if (presetPaymentMethod == null || form.getFieldValue("paymentMethod") != null) return;
    form.setFieldValue("paymentMethod", presetPaymentMethod);
    if (!walletDirty) walletInitializeDirty();
  });
  React.useEffect(() => {
    applyDefaultPaymentMethod();
  }, [form, presetPaymentMethod]);

  useDrawerBaselineEffect(() => { if (open) walletInitializeDirty(); }, [open]);
  if (!canWriteFinance) return null;

  const handleDelete = async () => {
    if (!walletEdit) return;

    const ok = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: text("Delete wallet"),
        content: text("Delete wallet confirm").replace("{0}", walletEdit.walletProductName || ""),
        okText: text("Delete"),
        okButtonProps: { danger: true },
        cancelText: text("Cancel"),
        onOk: () => resolve(true),
        onCancel: () => resolve(false)
      });
    });
    if (!ok) return;

    await api.del(`/api/v1/wallets/${encodeURIComponent(String(walletEdit.id))}`);
    message.success(text("Deleted."));
    onDeleted();
  };

  const handleSave = () =>
    (async () => {
      const v = await form.validateFields();
      const walletProductId = walletEdit ? walletEdit.walletProductId : v.walletProductId;
      if (!walletProductId) throw new Error(text("Please select a wallet product"));
      const balance = walletEdit == null ? Number(v.balance || 0) : null;
      if (balance != null && (Number.isNaN(balance) || balance < 0)) throw new Error(text("Invalid balance"));
      const validFromIso = formatDateOnlyIso(v.validFrom);
      const validToIso = formatDateOnlyIso(v.validTo);
      if (!validFromIso) throw new Error(text("Valid from is required"));
      const isInitialValueTopUp = balance != null && !isPointsWallet && balance > 0;
      if (isInitialValueTopUp && v.paymentMethod == null) {
        message.warning(text("WalletPaymentMethodRequired"));
        return;
      }

      setSaving(true);
      try {
        const nextIsLocked = isLocked;
        if (walletEdit) {
          await api.put(`/api/v1/wallets/${encodeURIComponent(String(walletEdit.id))}`, {
            usageLimitValue: v.usageLimitUnit != null ? (v.usageLimitValue != null ? Number(v.usageLimitValue) : null) : null,
            usageLimitUnit: v.usageLimitUnit != null ? Number(v.usageLimitUnit) : null,
            validFrom: validFromIso,
            validTo: validToIso || null,
            issuedStudioId: homeStudioId || null,
            accessScope: ACCESS_SCOPE_ANY_STUDIO, // AnyStudio
            allowedStudioIds: [],
            requiresNoArrears: true,
            allowsAccess: v.allowsAccess !== false,
            isManuallyLocked: nextIsLocked,
            note: null
          });
          message.success(text("Wallet saved."));
        } else {
          await api.post("/api/v1/wallets", {
            personId,
            walletProductId: v.walletProductId,
            balance: balance!,
            usageLimitValue: v.usageLimitUnit != null ? (v.usageLimitValue != null ? Number(v.usageLimitValue) : null) : null,
            usageLimitUnit: v.usageLimitUnit != null ? Number(v.usageLimitUnit) : null,
            validFrom: validFromIso,
            validTo: validToIso || null,
            issuedStudioId: homeStudioId || null,
            allowsAccess: v.allowsAccess !== false,
            accessScope: ACCESS_SCOPE_ANY_STUDIO, // AnyStudio
            allowedStudioIds: [],
            requiresNoArrears: true,
            isManuallyLocked: nextIsLocked,
            contractPackageId: null, // Formerly contractId
            note: null,
            paymentMethod: isInitialValueTopUp ? v.paymentMethod : null,
            sepaGroupId: null
          });
          if (isInitialValueTopUp) rememberPaymentMethod(v.paymentMethod);
          message.success(text("Wallet added."));
        }

        const updatedPerson = await loadPersonDetailRecord(api, personId).catch(() => null);
        onSaved(updatedPerson);
      } finally {
        setSaving(false);
      }
    })().catch(() => {});

  return (
    <FormDrawer dirty={walletDirty}
      title={walletEdit ? "Edit wallet" : "Add wallet"}
      open={open}
      onCancel={onClose}
      submitting={saving}
      submitText={walletEdit ? "Save" : "Create"}
      hideCancel={Boolean(walletEdit)}
      extra={
        <>
          <Checkbox checked={isLocked} onChange={(event) => setIsLocked(event.target.checked)}>{text("Manually locked")}</Checkbox>
          {walletEdit ? (
            <Tooltip
              title={
                walletEdit.canDelete === false
                  ? text("WalletCannotBeDeletedAlreadyBooked")
                  : undefined
              }
            >
              <span>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={walletEdit.canDelete === false}
                  onClick={handleDelete}
                >
                  {text("Delete")}
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </>
      }
      onSave={handleSave}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        {!walletEdit && (
          <Form.Item
            name="walletProductId"
            label={text("Wallet product")}
            rules={[{ required: !walletEdit, message: text("Please select") }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={walletProducts.map((w) => ({ value: w.id, label: w.name }))}
              placeholder={text("Select product")}
              disabled={Boolean(walletEdit)}
              onChange={(id) => {
                if (walletEdit) return;
                onWalletProductChange(String(id ?? ""));
                const p = walletProducts.find((x) => String(x.id) === String(id));
                const next: Record<string, unknown> = {};

                // Balance: only override if user hasn't manually touched it.
                if (!walletBalanceTouched) {
                  const n =
                    p?.defaultBalance != null
                      ? Number(p.defaultBalance)
                      : null;
                  next.balance = n && n !== 0 ? n : undefined;
                }

                // ValidTo: only override if user hasn't touched it.
                if (!form.isFieldTouched("validTo")) {
                  const validFrom: Dayjs = form.getFieldValue("validFrom") || dayjs();
                  const vv = p?.defaultValidValue;
                  const vu = p?.defaultValidUnit;
                  let vt: Dayjs | null = null;
                  if (vv != null && vu != null) {
                    const n = Number(vv);
                    const u = Number(vu);
                    if (Number.isFinite(n) && n > 0) {
                      if (u === TIME_UNIT_DAYS) vt = dayjs(validFrom).add(n, "day");
                      if (u === TIME_UNIT_WEEKS) vt = dayjs(validFrom).add(n, "week");
                      if (u === TIME_UNIT_MONTHS) vt = dayjs(validFrom).add(n, "month");
                      if (u === TIME_UNIT_YEARS) vt = dayjs(validFrom).add(n, "year");
                    }
                  }
                  next.validTo = vt;
                }

                // Usage limit: only override if user hasn't touched it.
                if (!form.isFieldTouched("usageLimitValue")) next.usageLimitValue = p?.defaultUsageLimitValue ?? null;
                if (!form.isFieldTouched("usageLimitUnit")) next.usageLimitUnit = p?.defaultUsageLimitUnit ?? null;

                // Access: default depends on product; for currency/value wallets default to NO access unless explicitly enabled.
                if (!form.isFieldTouched("allowsAccess")) {
                  const type = Number(p?.type ?? 0); // 1=Points, 2=Value
                  const productAllows = p?.allowsAccess;
                  next.allowsAccess = type === WALLET_TYPE_VALUE ? false : (productAllows !== false);
                }

                form.setFieldsValue(next);
              }}
            />
          </Form.Item>
        )}

        {walletEdit ? (
          <div
            style={{
              marginBottom: spacing.lg,
              padding: cssSpacing(spacing.relaxed, spacing.lg),
              border: "1px solid rgba(5,5,5,0.08)",
              borderRadius: designBorderRadius.lg,
              background: "rgba(5,5,5,0.02)"
            }}
          >
            <Typography.Text type="secondary" style={{ display: "block", marginBottom: spacing.xs }}>
              {text("Balance")}
            </Typography.Text>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {formatWalletBalance(currentBalance, walletType, units)}
            </Typography.Title>
          </div>
        ) : null}
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: spacing.md, marginTop: designSpacing.controlCompact }}>
          {walletEdit ? null : (
            <Form.Item
              name="balance"
              label={text("Balance")}
              rules={[{ required: true, message: text("Balance is required") }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={isPointsWallet ? 1 : 0.01}
                decimalSeparator={decimalSeparator}
                addonAfter={isPointsWallet ? text("Points") : currencyCode}
                placeholder={isPointsWallet ? formatNumber(0, units) : moneyPlaceholder}
                onChange={(v) => setWalletBalanceTouched(v != null)}
              />
            </Form.Item>
          )}
          {!walletEdit && !isPointsWallet && Number(watchedBalance || 0) > 0 ? (
            <Form.Item
              name="paymentMethod"
              label={text("Payment method")}
              rules={[{ required: true, message: text("WalletPaymentMethodRequired") }]}
            >
              <Select
                placeholder={text("Please select")}
                options={paymentMethodOptions}
              />
            </Form.Item>
          ) : null}
          {walletEdit && (() => {
            const editedWalletType = Number(walletEdit.walletType || 0);
            const isPoints = editedWalletType === 1;
            // Geld-Wertekonto: Zahlungsart ist Pflicht beim Buchen.
            const paymentMethodMissing = !isPoints && effectiveAdjustPaymentMethod == null;

            // Ein Betrag mit Vorzeichen: positiv = auf-/einbuchen, negativ = ab-/auszahlen.
            const runAdjust = async () => {
              if (!adjustAmount || adjustAmount === 0) {
                message.warning(text("Please enter an amount"));
                return;
              }
              if (paymentMethodMissing) {
                message.warning(text("WalletPaymentMethodRequired"));
                return;
              }
              if (adjustAmount < 0) {
                const latestBalance = Number(form.getFieldValue("balance") ?? walletEdit.balance ?? 0);
                if (Math.abs(adjustAmount) > latestBalance) {
                  message.error(text("Amount must not exceed balance."));
                  return;
                }
              }
              const successMsg = adjustAmount > 0
                ? (isPoints ? text("Balance credited.") : text("Balance deposited."))
                : (isPoints ? text("Balance debited.") : text("Balance withdrawn."));
              try {
                await api.post(`/api/v1/wallets/${encodeURIComponent(String(walletEdit.id))}/adjust-balance`, {
                  amount: adjustAmount,
                  paymentMethod: isPoints ? null : effectiveAdjustPaymentMethod
                });
                if (!isPoints) rememberPaymentMethod(effectiveAdjustPaymentMethod);
                message.success(successMsg);
                setAdjustAmount(null);
                setAdjustPaymentMethod(null);
                await onReloadWalletAndHistory();
                onReloadWalletsAfterBalanceAdjustment();
              } catch {
                // Central API toast shows error
              }
            };

            return (
              <div style={{ display: "flex", gap: spacing.formItemGap, alignItems: "flex-start" }}>
                <Form.Item
                  label={text("Adjust amount")}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    step={isPoints ? 1 : 0.01}
                    decimalSeparator={decimalSeparator}
                    addonAfter={isPoints ? text("Points") : currencyCode}
                    placeholder={isPoints ? formatNumber(0, units) : moneyPlaceholder}
                    value={adjustAmount}
                    onChange={(v) => setAdjustAmount(typeof v === "number" ? v : null)}
                    onPressEnter={() => { void runAdjust(); }}
                  />
                </Form.Item>
                {!isPoints && (
                  <Form.Item label={text("Payment method")} style={{ flex: 1, marginBottom: 0 }}>
                    <Select
                      style={{ width: "100%" }}
                      placeholder={text("Please select")}
                      options={paymentMethodOptions}
                      value={effectiveAdjustPaymentMethod ?? undefined}
                      onChange={(v) => setAdjustPaymentMethod(typeof v === "number" ? v : null)}
                      allowClear
                    />
                  </Form.Item>
                )}
                <Form.Item label=" " style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    disabled={paymentMethodMissing}
                    onClick={() => { return runAdjust(); }}
                  >
                    {text("Post entry")}
                  </Button>
                </Form.Item>
              </div>
            );
          })()}
        </div>

        {walletEdit && (
          <PersonWalletHistorySection
            history={history}
            loading={historyLoading}
            walletType={walletEdit.walletType}
            currencyCode={currencyCode}
            text={text}
            units={units}
          />
        )}

        <Form.Item
          name="isLocked"
          valuePropName="checked"
          extra={text("Locked wallets cannot be used.")}
          style={{ display: "none" }}>
          <Checkbox>{text("Locked")}</Checkbox>
        </Form.Item>

        <FormSectionHeading title={text("Period")} sectionKey="person-wallet-edit.period">
          <div style={{ marginTop: designSpacing.controlCompact }}>
          <ResponsiveFormGrid>
            <Form.Item
              name="validFrom"
              label={text("Valid from")}
              rules={[{ required: true, message: text("Valid from is required") }]}
            >
              <DatePicker allowClear placeholder={text("Select date")} format={dateInputFormat} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="validTo" label={text("Valid until (optional)")}>
              <DatePicker allowClear placeholder={text("Select date")} format={dateInputFormat} style={{ width: "100%" }} />
            </Form.Item>
          </ResponsiveFormGrid>
          </div>
        </FormSectionHeading>
      </Form>
    </FormDrawer>
  );
}
